import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CreateShortTermParkingRequest, ParkingTypeService, ParkingSpaceDto, ParkingZoneDto } from '../../../../../api/gen';

export interface ShortTermTypeResult {
  success: boolean;
  data?: any;
}

@Component({
  selector: 'app-add-short-term-type',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './add-short-term-type.component.html',
  styleUrl: './add-short-term-type.component.scss'
})
export class AddShortTermTypeComponent implements OnInit {
  private fb = inject(FormBuilder);
  private parkingTypeService = inject(ParkingTypeService);
  private snackBar = inject(MatSnackBar);

  @Input({ required: true }) parkingOperatorId: string | undefined;
  @Input() parkingSpace!: ParkingSpaceDto;
  @Output() submitted = new EventEmitter<ShortTermTypeResult>();

  form!: FormGroup;
  isLoading = signal(false);
  availableZones = signal<ParkingZoneDto[]>([]);

  ngOnInit(): void {
    this.initForm();
    this.availableZones.set(this.parkingSpace?.parkingZones ?? []);
  }

  private initForm(): void {
    this.form = this.fb.group({
      availableForParkingZones: [[] as string[]],
      availableFrom: [null],
      availableTo: [null]
    });
  }

  submit(): void {
    this.isLoading.set(true);

    const formValue = this.form.value;
    const zoneIds: string[] = formValue.availableForParkingZones ?? [];

    const request: CreateShortTermParkingRequest = {
      availableFrom: formValue.availableFrom ? new Date(formValue.availableFrom).toISOString() : null,
      availableTo: formValue.availableTo ? new Date(formValue.availableTo).toISOString() : null,
      availableForParkingSpaces: this.parkingSpace?.id ? [this.parkingSpace.id] : [],
      // Empty means the whole space, which is why it is sent as an empty list rather than null:
      // the gate reads this list per zone, so picking zones here is what decides where a visitor
      // can be admitted and charged. Leaving it empty keeps the type on sale everywhere.
      availableForParkingZones: zoneIds,
      organizationId: null
    };

    this.parkingTypeService.parkingTypesShortTermParkersPost(this.parkingOperatorId, request).subscribe({
      next: (result) => {
        this.snackBar.open('Short-term parking type created successfully', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.submitted.emit({ success: true, data: result });
      },
      error: (error) => {
        console.error('Error creating short-term type:', error);
        this.snackBar.open('Failed to create short-term type', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.submitted.emit({ success: false });
      }
    });
  }
}
